import { Dispatch, SetStateAction } from "react";
import { css, keyframes } from "@emotion/react";
import { IonCheckbox, IonItem, IonToggle } from "@ionic/react";

import { translate } from "~utils";

import { SpedDetailsType } from "./types";

const flashOnce = keyframes`
  0% { background-color: var(--swing-purple200); }
  95% { background-color: var(--swing-purple200); }
  100% { background-color: transparent; }
`;

type SpedDetailsProps = {
  value: SpedDetailsType;
  onChange: Dispatch<SetStateAction<SpedDetailsType>>;
  disabled: boolean;
};

type ArrayField = "supportLevel" | "setting";

type ArrayItem<K extends ArrayField> = SpedDetailsType[K][number];

export function SpedDetails(props: SpedDetailsProps) {
  const { onChange, value, disabled } = props;

  const toggleArrayValue = <K extends ArrayField>(key: K, item: ArrayItem<K>) => {
    onChange((old) => {
      const current = (old[key] ?? []) as Array<ArrayItem<K>>;
      const next = current.includes(item) ? current.filter((x) => x !== item) : [...current, item];
      return {
        ...old,
        [key]: next,
      };
    });
  };

  const renderCheckbox = <K extends ArrayField>(props: {
    field: K;
    value: ArrayItem<K>;
    label: string;
    description?: string;
  }) => (
    <IonCheckbox
      checked={(value[props.field] as Array<ArrayItem<K>>)?.includes(props.value)}
      css={{ padding: 16 }}
      onIonChange={() => toggleArrayValue(props.field, props.value)}
      labelPlacement="end"
      disabled={disabled}
    >
      <div>
        <p css={{ margin: 0 }}>{props.label}</p>
        <p css={{ margin: 0, fontSize: 12 }}>{props.description}</p>
      </div>
    </IonCheckbox>
  );

  return (
    <div
      css={css`
        width: 100%;
        animation: ${flashOnce} 3.5s ease-in-out 1;
        border-radius: 8px;
        padding: 16px;
        border-bottom: none;
      `}
    >
      <p css={{ fontWeight: "bold", marginTop: 0 }}>{translate("SPED_SUPPORT_LEVEL")}</p>
      <div css={{ display: "flex", flexDirection: "column" }}>
        {renderCheckbox({
          field: "supportLevel",
          value: "MILD",
          label: translate("SPED_SUPPORT_LEVEL_MILD"),
        })}

        {renderCheckbox({
          field: "supportLevel",
          value: "SEVERE",
          label: translate("SPED_SUPPORT_LEVEL_SEVERE"),
        })}

        <p css={{ fontWeight: "bold" }}>{translate("SPED_SETTING")}</p>

        {renderCheckbox({
          field: "setting",
          value: "CO_TEACHING",
          label: translate("SPED_SETTING_TEACHING_HEADER"),
          description: translate("SPED_SETTING_TEACHING_LABEL"),
        })}

        {renderCheckbox({
          field: "setting",
          value: "PUSH_IN",
          label: translate("SPED_SETTING_PUSH_IN_HEADER"),
          description: translate("SPED_SETTING_PUSH_IN_LABEL"),
        })}

        {renderCheckbox({
          field: "setting",
          value: "PULL_OUT",
          label: translate("SPED_SETTING_PULL_OUT_HEADER"),
          description: translate("SPED_SETTING_PULL_OUT_LABEL"),
        })}

        {renderCheckbox({
          field: "setting",
          value: "SDC",
          label: translate("SPED_SETTING_SDC_HEADER"),
          description: translate("SPED_SETTING_SDC_LABEL"),
        })}
      </div>
      <IonItem
        lines="none"
        css={{ "--ion-item-background": "transparent", background: "transparent" }}
      >
        <IonToggle
          checked={value.toileting}
          onIonChange={(e) =>
            onChange((old) => ({
              ...old,
              toileting: e.detail.checked,
            }))
          }
          css={{ fontWeight: "bold", background: "none" }}
          disabled={disabled}
        >
          {translate("SPED_TOILETING_REQUIRED")}
        </IonToggle>
      </IonItem>
    </div>
  );
}
